import {
  AlertType,
  BoardGroupFragment,
  BoardAlertFragment,
  PlanItemDetailsFragment,
  AlertLevel
} from '../../../../generated/graphql'
import { DetailsPlanAlert } from '../../../../types'

/*
  Get all Alerts for a Plan Item for display
*/
// eslint-disable-next-line import/prefer-default-export
export function getPlanItemAlerts(
  itemAlerts: Array<BoardAlertFragment>,
  planItem: PlanItemDetailsFragment | undefined,
  group: BoardGroupFragment | undefined,
  courseLabel: string,
  advisorLabel: string,
  termLabel: string,
  courseTakenRequirementMessage: string,
  duplicateCourseMessage: string
): Array<DetailsPlanAlert> {
  if (!planItem || !itemAlerts) {
    return []
  }

  const showOptionalMessageForRequirements =
    courseTakenRequirementMessage &&
    courseTakenRequirementMessage.length > 0 &&
    (planItem.usedToMeetRequirement || planItem.courseTakenInReq) &&
    !planItem.inProgress

  const alerts: Array<DetailsPlanAlert> | undefined = itemAlerts
    .map(({ alertType, alertKey }) => {
      if (alertType === AlertType.DuplicateCourse) {
        const message = planItem.isCoursePlaceholder
          ? `clear this ${courseLabel.toLowerCase()} selection`
          : 'delete this duplicate'
        return {
          alertKey,
          alertType,
          level: AlertLevel.Warning,
          itemId: planItem.id,
          planId: planItem.planId,
          title: `Duplicate ${courseLabel}`,
          description:
            duplicateCourseMessage ||
            `This ${courseLabel.toLowerCase()} is on your plan multiple times. Do you want to ${message}?`
        }
      }

      if (alertType === AlertType.InactiveCourse) {
        return {
          alertKey,
          alertType,
          level: AlertLevel.Warning,
          itemId: planItem.id,
          planId: planItem.planId,
          title: `${courseLabel} no longer offered or available.`,
          description: `${
            planItem.isCoursePlaceholder ? 'Clear' : 'Delete'
          } this ${courseLabel.toLowerCase()} and add an alternative ${courseLabel.toLowerCase()}.  Please discuss ${courseLabel.toLowerCase()} options with your ${advisorLabel.toLowerCase()}.`
        }
      }

      if (alertType === AlertType.Requisite) {
        return {
          alertKey,
          alertType,
          level: AlertLevel.Warning,
          itemId: planItem.id,
          planId: planItem.planId,
          title: `This ${courseLabel.toLowerCase()} has a requisite.`,
          description: `Make sure you are eligible for this ${courseLabel.toLowerCase()} before scheduling.  If you've already completed or planned the requisite, dismiss this alert.`
        }
      }

      if (group && alertType === AlertType.Unavailable) {
        return {
          alertKey,
          alertType,
          level: AlertLevel.Warning,
          itemId: planItem.id,
          planId: planItem.planId,
          title: `${courseLabel} may not be available this ${termLabel.toLowerCase()}`,
          description: `This ${courseLabel.toLowerCase()} may not be available for ${
            group.title
          }. Check ${courseLabel.toLowerCase()} offerings before scheduling.`
        }
      }

      if (alertType === AlertType.ItemMeetsRequirement) {
        return {
          alertKey,
          alertType,
          level: AlertLevel.Success,
          itemId: planItem.id,
          planId: planItem.planId,
          title: `${courseLabel} taken`,
          description: showOptionalMessageForRequirements
            ? courseTakenRequirementMessage
            : `Satisfied <b>${planItem.requirementTitles.join(', ')}</b> ${
                planItem.requirementTitles.length === 1 ? 'requirement' : 'requirements'
              }.`
        }
      }

      if (alertType === AlertType.ItemDoesNotMeetRequirement) {
        return {
          alertKey,
          alertType,
          level: AlertLevel.Warning,
          itemId: planItem.id,
          planId: planItem.planId,
          title: `${courseLabel} did not satisfy any requirements`,
          description: `${courseLabel} did not satisfy any requirements.`
        }
      }

      if (alertType === AlertType.InProgressCourse) {
        return {
          alertKey,
          alertType,
          level: AlertLevel.Success,
          itemId: planItem.id,
          planId: planItem.planId,
          title: `${courseLabel} is in progress`,
          description: `Will satisfy <b>${planItem.requirementTitles.join(', ')}</b> ${
            planItem.requirementTitles.length === 1 ? 'requirement' : 'requirements'
          }.`
        }
      }

      return {
        alertKey: 'NOT_IMPLEMENTED',
        alertType: AlertType.DuplicateCourse,
        level: AlertLevel.Warning,
        itemId: '',
        planId: '',
        title: '',
        description: ''
      }
    })
    .filter((a) => a.alertKey !== 'NOT_IMPLEMENTED')

  return alerts ?? []
}
